import { inject } from "fw";
import { Store, handle } from "fw-state";

import { AtsOrganizationTemplate } from "models/ats-organization-template";
import { FormErrorHandling } from "./error-handling";
import {
    AtsTemplateRepository,
    AtsOrganizationTemplatePostArgs,
    AtsOrganizationTemplatePutArgs,
    ManualOrganizationTemplateArgs
} from "network/ats-template-repository";

type Shape = {
    templateHash: { [id: string]: AtsOrganizationTemplate };
};

export class EnsureTemplateAction {
    constructor(public ids: string[]) {}
}

export class AddTemplateAction {
    public template: AtsOrganizationTemplate;

    constructor(public args: AtsOrganizationTemplatePostArgs) {}
}

export class AddManualTemplateAction {
    constructor(
        public name: string,
        public template: AtsOrganizationTemplate
    ) {}
}

export class UpdateTemplateAction {
    constructor(
        public id: string,
        public args: AtsOrganizationTemplatePutArgs
    ) {}
}

export class UpdateManualTemplateAction {
    constructor(
        public id: string,
        public args: ManualOrganizationTemplateArgs
    ) {}
}

export class DeleteTemplateAction {
    constructor(public id: string) {}
}

export class LoadSeasonConfigurationAction {
    public seasonConfiguration: any = null;

    constructor(public id: string = null) {}
}

@inject
export class AtsOrganizationTemplateStore extends Store<Shape> {
    constructor(private atsTemplateRepo: AtsTemplateRepository) {
        super();
    }

    defaultState() {
        return {
            templateHash: {}
        };
    }

    @handle(AddTemplateAction)
    private async handleAddTemplateAction(action: AddTemplateAction) {
        const template = await this.atsTemplateRepo.post(action.args);
        action.template = template;
        this.state.templateHash[template.Id] = template;
        this.setState((s) => s);
    }

    @handle(AddManualTemplateAction)
    private async handleAddManualTemplateAction(action: AddManualTemplateAction) {
        const template = await this.atsTemplateRepo.postManual({ Name: action.name, SeasonConfiguration: action.template });
        action.template = template;
        this.state.templateHash[template.Id] = template;
        this.setState((s) => s);
    }

    @handle(UpdateTemplateAction)
    private async handleUpdateTemplateAction(action: UpdateTemplateAction) {
        const template = await this.atsTemplateRepo.put(action.id, action.args);
        this.state.templateHash[template.Id] = template;
        this.setState((s) => s);
    }

    @handle(UpdateManualTemplateAction)
    private async handleUpdateManualTemplateAction(action: UpdateManualTemplateAction) {
        const template = await this.atsTemplateRepo.putManual(action.id, action.args);
        this.state.templateHash[template.Id] = template;
        this.setState((s) => s);
    }

    @handle(DeleteTemplateAction)
    private async handleDeleteTemplateAction(action: DeleteTemplateAction) {
        await this.atsTemplateRepo.remove(action.id);
        delete this.state.templateHash[action.id];
        this.setState((s) => s);
    }

    @handle(EnsureTemplateAction)
    private async handleEnsureTemplateAction(action: EnsureTemplateAction) {
        const missing = action.ids.filter((id) => this.state.templateHash[id] == null);
        if (missing.length === 0) return;

        const templates = await this.atsTemplateRepo.listByIds(missing);
        for (const template of templates) this.state.templateHash[template.Id] = template;

        this.setState((s) => s);
    }

    @handle(LoadSeasonConfigurationAction, FormErrorHandling)
    private async handleGetSeasonConfigurationAction(action: LoadSeasonConfigurationAction) {
        action.seasonConfiguration = await this.atsTemplateRepo.getConfigurationBySeasonId(action.id);
    }
}
